import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import { QUOTATION_STATUS_LABELS, INVOICE_STATUS_LABELS } from "config/statusLabels";
import { useAbacPermission } from "hooks/useAbacPermission";
import { RESOURCE_TYPES, ACTIONS } from "utils/abacConstants";
// Same PRO-chart tokens the FixedOps Inventory / LCP settings tables use so
// this page reads as the same visual language as /fixedops/inventory/items.
import { cc, panelSx } from "afms/theme/commandCenterTheme";
import SendQuotationModal from "./SendQuotationModal";
import SendInvoiceModal from "./SendInvoiceModal";
import DeleteQuotationModal from "./DeleteQuotationModal";

// Status → chip colours. Keys are the numeric quotation status
// (0 Draft, 1 Sent, 2 Accepted, 3 Paid, 4 Overdue).
const STATUS_CHIP_STYLE = {
  0: { bg: "#f1f5f9", fg: "#475569" },
  1: { bg: "#dbeafe", fg: "#1d4ed8" },
  2: { bg: "#fef9c3", fg: "#854d0e" },
  3: { bg: "#dcfce7", fg: "#15803d" },
  4: { bg: "#fee2e2", fg: "#b91c1c" },
  5: { bg: "#f1f5f9", fg: "#475569" },
};

const HEAD_CELLS = [
  { id: "id", label: "Quote ID", align: "left", width: 110 },
  { id: "customerName", label: "Customer", align: "left" },
  { id: "products", label: "Products", align: "left", width: "38%" },
  { id: "total", label: "Total", align: "right", width: 120 },
  { id: "status", label: "Status", align: "left", width: 120 },
  { id: "actions", label: "Actions", align: "right", width: 250 },
];

const getCustomerName = (quote) => {
  if (quote?.customerName) return quote.customerName;
  const fullName = [quote?.billingAddress?.firstName, quote?.billingAddress?.lastName]
    .filter(Boolean)
    .join(" ");
  return fullName || quote?.billingAddress?.name || "-";
};

const QuoteListing = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const [sending, setSending] = useState(null);
  const [invoicing, setInvoicing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // Same capability the Send Quote action beside it needs. Deliberately not
  // Invoice:Create — that's OWNER-only, which would let vendor staff quote a
  // customer but not bill them, with no explanation for the missing button.
  const { granted: canInvoice } = useAbacPermission(
    RESOURCE_TYPES.QUOTATION,
    ACTIONS.CREATE
  );

  // Rendered server-side (QuestPDF) rather than in the browser, so this is
  // byte-identical to the copy the send dialog attaches.
  const handleDownload = async (quote) => {
    if (downloadingId) return;
    try {
      setDownloadingId(quote.id);
      const response = await AdminQuotationApiService.getQuotationPdf(quote.id);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `quotation-${quote.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Failed to build the PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  // 2 = Accepted, 3 = Paid — past the point where editing or deleting the
  // document makes sense. The server enforces the same rule.
  const isLocked = (quote) => quote?.status === 2 || quote?.status === 3;

  // Why the invoice action can be unavailable. Null = go ahead.
  //   • A cancelled quote isn't billable.
  //   • An invoice that's been paid or voided is a record of something that
  //     happened; the server refuses to re-send it.
  //   • No customer org means the invoice would reach nobody's Payables,
  //     since that page matches on recipientOrganizationId.
  const invoiceBlockedReason = (quote) => {
    if (quote?.status === 5) return "Cancelled quotes can't be invoiced";
    const invoiceStatus = quote?.invoice?.status;
    if (invoiceStatus === 3) return "This quote's invoice has already been paid";
    if (invoiceStatus === 5) return "This quote's invoice was cancelled";
    if (!quote?.customerOrganisationId) {
      return "No customer organisation on this quote — add a billing address first";
    }
    return null;
  };

  const loadQuotes = async () => {
    try {
      setLoading(true);
      // Admin/seller quotations source with no status filter so this page
      // shows the org's own quotes — including Draft (status 0) ones created
      // from an inspection.
      const response = await AdminQuotationApiService.getQuotations({
        pageNumber: 1,
        pageSize: 200,
      });
      const payload = response?.data?.data?.data ?? response?.data?.data ?? [];
      setQuotes(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error(error);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotes();
  }, []);

  const paged = useMemo(
    () => quotes.slice(page * pageSize, page * pageSize + pageSize),
    [quotes, page, pageSize]
  );

  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const statusChip = (status) => {
    const style = STATUS_CHIP_STYLE[Number(status)] || STATUS_CHIP_STYLE[0];
    const label =
      QUOTATION_STATUS_LABELS.find((item) => Number(item.value) === Number(status))
        ?.label || `Status ${status}`;
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 700,
          bgcolor: style.bg,
          color: style.fg,
        }}
      />
    );
  };

  // Shown beside the quote status once an invoice exists against the quote.
  // Carries the invoice's own status rather than a bare "Invoiced" flag — a
  // draft that was never sent shouldn't read the same as one sitting in the
  // customer's Payables.
  const invoiceChip = (quote) => {
    if (!quote?.invoiceId) return null;
    const status = quote?.invoice?.status;
    const label =
      INVOICE_STATUS_LABELS.find((item) => Number(item.value) === Number(status))?.label;
    return (
      <Chip
        label={label ? `Invoice · ${label}` : "Invoiced"}
        size="small"
        variant="outlined"
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 700,
          ml: 0.75,
          borderColor: cc.line,
          color: cc.muted,
        }}
      />
    );
  };

  return (
    <DashboardLayout>
      {/* Full-width per the full-width-page convention. The 20px gutter comes
          from DashboardLayout, which already applies pl/pr 20px for pages in
          the FixedOps shell — adding another px 2.5 here stacked two wrappers
          and doubled the gap to 40px on each side. Vertical padding stays;
          nothing else supplies it. */}
      <SoftBox py={3} px={0}>
        <Card sx={{ ...panelSx, overflow: "hidden", p: 0 }}>
          {/* PRO-chart header strip — title + Create button, same px/pt/pb
              spacing as /fixedops/inventory/items. */}
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2,
              borderBottom: `1px solid ${cc.line}`,
              bgcolor: cc.page,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
            >
              <SoftTypography variant="h6" fontWeight="bold" sx={{ color: cc.ink }}>
                Quotations
              </SoftTypography>
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate("/admin/quotations/create")}
              >
                Create New Quote
              </SoftButton>
            </Stack>
          </Box>

          {/* PRO-chart body — flat table under the bordered strip, padded
              inward so it doesn't kiss the Card edge. */}
          <Box sx={{ px: 3, pt: 2, pb: 3 }}>
            {loading ? (
              <Skeleton variant="rectangular" height={300} animation="wave" />
            ) : (
              <>
                <TableContainer sx={{ boxShadow: "none" }}>
                  <Table sx={{ minWidth: 720 }} aria-label="quotations table">
                    {/* Bare TableHead with display:table-header-group to
                        override SoftUI's display:block default. */}
                    <TableHead
                      sx={{ display: "table-header-group", p: 0, borderRadius: 0 }}
                    >
                      <TableRow>
                        {HEAD_CELLS.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={column.width ? { width: column.width } : undefined}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paged.length === 0 && (
                        <TableRow
                          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            colSpan={HEAD_CELLS.length}
                            align="center"
                          >
                            No quotations yet.
                          </TableCell>
                        </TableRow>
                      )}

                      {paged.map((quote) => (
                        <TableRow
                          key={quote.id}
                          hover
                          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell align="left">
                            <SoftTypography variant="button" fontWeight="medium">
                              {quote.id}
                            </SoftTypography>
                          </TableCell>
                          <TableCell align="left">
                            <SoftTypography variant="caption" color="text">
                              {getCustomerName(quote)}
                            </SoftTypography>
                          </TableCell>
                          <TableCell align="left" sx={{ whiteSpace: "normal" }}>
                            {(quote.items || []).length === 0 ? (
                              <SoftTypography variant="caption" color="text">
                                --
                              </SoftTypography>
                            ) : (
                              (quote.items || []).map((item) => (
                                <SoftTypography
                                  key={item.id || `${item.name}-${item.templateId || "na"}`}
                                  variant="caption"
                                  color="text"
                                  display="block"
                                >
                                  {item.name} ×{item.quantity || 1}
                                </SoftTypography>
                              ))
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <SoftTypography variant="button" fontWeight="medium">
                              ${Number(quote.amount || 0).toFixed(2)}
                            </SoftTypography>
                          </TableCell>
                          <TableCell align="left">
                            {statusChip(quote.status)}
                            {invoiceChip(quote)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(`/user/quotations/${quote.id}/details`)
                                }
                                aria-label={`View quote ${quote.id}`}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                isLocked(quote)
                                  ? "Accepted and paid quotes can't be edited"
                                  : "Edit quote"
                              }
                            >
                              {/* span wrapper — a disabled IconButton fires no
                                  events, so Tooltip needs a live child. */}
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isLocked(quote)}
                                  onClick={() =>
                                    navigate(`/admin/quotations/create?quoteId=${quote.id}`)
                                  }
                                  aria-label={`Edit quote ${quote.id}`}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Email to the customer">
                              <IconButton
                                size="small"
                                onClick={() => setSending(quote)}
                                aria-label={`Send quote ${quote.id}`}
                              >
                                <SendOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canInvoice && (
                              <Tooltip
                                title={invoiceBlockedReason(quote) || "Send invoice"}
                              >
                                {/* span wrapper — a disabled IconButton fires
                                    no events, so Tooltip needs a live child. */}
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={Boolean(invoiceBlockedReason(quote))}
                                    onClick={() => setInvoicing(quote)}
                                    aria-label={`Send invoice for quote ${quote.id}`}
                                  >
                                    <ReceiptLongOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Download PDF">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={downloadingId === quote.id}
                                  onClick={() => handleDownload(quote)}
                                  aria-label={`Download quote ${quote.id}`}
                                >
                                  <DownloadOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                isLocked(quote)
                                  ? "Accepted and paid quotes can't be deleted"
                                  : "Delete quote"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isLocked(quote)}
                                  onClick={() => setDeleting(quote)}
                                  aria-label={`Delete quote ${quote.id}`}
                                  sx={{ color: isLocked(quote) ? undefined : cc.red }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={quotes.length}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Box>
        </Card>
      </SoftBox>
      <SendQuotationModal
        open={Boolean(sending)}
        onClose={() => setSending(null)}
        onSent={loadQuotes}
        quote={sending}
      />
      <SendInvoiceModal
        open={Boolean(invoicing)}
        onClose={() => setInvoicing(null)}
        onSent={loadQuotes}
        quote={invoicing}
      />
      <DeleteQuotationModal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onDeleted={loadQuotes}
        quote={deleting}
      />
    </DashboardLayout>
  );
};

export default QuoteListing;
